import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Logo, LogoMark } from "@/components/Logo";
import PortalSwitch from "@/components/PortalSwitch";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Users, ArrowLeftRight, MessageSquare, FileText, LogOut, Menu, ChevronRight, Sun, Moon, ExternalLink,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/funds", label: "Funds", icon: ArrowLeftRight },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare },
  { to: "/admin/welcome", label: "Welcome Note", icon: FileText },
];

const AdminNavItems = ({ onNavigate }) => (
  <nav className="flex-1 px-3 space-y-1">
    {NAV.map((n) => (
      <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate}
        data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors duration-200 ${
            isActive
              ? "bg-white/10 text-white font-medium"
              : "text-zinc-400 hover:bg-white/5 hover:text-zinc-200"
          }`
        }>
        <n.icon className="size-4" /> {n.label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const initials = (user?.name || "A").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-background flex">
      {/* Desktop sidebar — dark graphite for admin distinction */}
      <aside className="hidden lg:flex w-[280px] shrink-0 flex-col h-dvh sticky top-0 bg-[#0F1115] border-r border-white/[0.06]">
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/[0.06]">
          <Logo dark />
          <Badge data-testid="admin-role-badge" className="rounded-full bg-brand/15 text-brand border-0 text-[10px] font-semibold tracking-wider px-2 py-0.5">ADMIN</Badge>
        </div>
        <div className="flex-1 py-4 overflow-y-auto"><AdminNavItems /></div>
        <div className="px-3 pb-3">
          <NavLink to="/portal" data-testid="admin-goto-portal" className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-white/5 transition-colors duration-200">
            <ExternalLink className="size-3.5" /> View Client Portal
          </NavLink>
        </div>
        <div className="p-3 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="size-9"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-brand/20 text-brand text-xs">{initials}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="h-16 border-b glass sticky top-0 z-40 flex items-center justify-between gap-2 px-3 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button data-testid="admin-mobile-menu-button" variant="ghost" size="icon" className="lg:hidden"><Menu className="size-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" data-testid="admin-mobile-nav-sheet" className="p-0 w-[280px] bg-[#0F1115] border-r border-white/[0.06]">
                <div className="h-16 flex items-center justify-between px-5 border-b border-white/[0.06]">
                  <Logo dark />
                  <Badge className="rounded-full bg-brand/15 text-brand border-0 text-[10px] font-semibold tracking-wider px-2 py-0.5">ADMIN</Badge>
                </div>
                <div className="py-4"><AdminNavItems onNavigate={() => setMobileOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            <LogoMark className="lg:hidden size-8" />
            <Badge data-testid="admin-badge" variant="outline" className="hidden md:inline-flex rounded-full bg-zinc-900 text-zinc-100 border-zinc-700 text-[11px] font-semibold tracking-wider">
              ADMIN CONSOLE
            </Badge>
            <PortalSwitch />
          </div>
          <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
            <NotificationBell variant="admin" />
            <button data-testid="admin-theme-toggle" onClick={toggle} title="Toggle dark mode" className="size-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors duration-200 text-muted-foreground">
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="admin-user-menu" className="flex items-center gap-2 rounded-full pr-2 hover:bg-muted transition-colors duration-200 p-1">
                  <Avatar className="size-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-brand/10 text-brand text-xs">{initials}</AvatarFallback></Avatar>
                  <span className="hidden sm:block text-sm font-medium">{user?.name?.split(" ")[0]}</span>
                  <ChevronRight className="hidden sm:block size-4 text-muted-foreground rotate-90" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="admin-logout-button" onClick={doLogout} className="text-destructive focus:text-destructive"><LogOut className="size-4 mr-2" /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 max-w-[1400px] w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
